'use client';

import React, { FC, useEffect, useState } from 'react';

import classNames from 'classnames';

import { UniversalLink } from '@/components/common/UniversalLink';
import { NavColumn } from '@/components/nav-v2';
import { Image } from '@/components/ui';

import { TypeNavGroup } from '@/core/generated-types';

import { Link, usePathname } from '@/navigation';

import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  NAV_EVENT_TYPES,
  PAGE_LOCATIONS,
  trackElementPresented,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

import generateTranslationsObject from '@/utils/translations/generateTranslationObject';

import { useDebouncedHover } from '../hooks/useDebouncedHover';

import { Cart } from '../Cart/Cart';
import { NavComponentProps } from '../GlobalNav';

import UserMenu from './UserMenu/UserMenu';

const MAX_NAV_COLUMN_COUNT = 5;

export const LargeNav: FC<NavComponentProps> = ({
  logo,
  groups,
  signInLink,
  linkMenuLoggedIn,
  i18n,
  isSticky,
  buttonCta,
  containerWidth = '1200px',
}) => {
  const [focusedGroup, setFocusedGroup] = useState<TypeNavGroup | null>(null);

  const translations = generateTranslationsObject(i18n?.fields);
  const pathname = usePathname();

  const { handleMouseEnter, handleMouseLeave } =
    useDebouncedHover<TypeNavGroup>({
      onShow: (group) => {
        setFocusedGroup(group ?? focusedGroup);

        group?.fields.title &&
          trackElementPresented({
            name: NAV_EVENT_TYPES.PRIMARY_NAV_EXPANDED,
            title: group?.fields.title,
          });
      },
      onHide: () => setFocusedGroup(null),
    });

  // Close menu when route changes
  useEffect(() => {
    if (focusedGroup) {
      setFocusedGroup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <div
      className={classNames(
        'sticky top-0 z-50 transition-shadow duration-300',
        {
          'shadow-lg': isSticky,
        },
      )}
    >
      {/* Nav bar */}
      <nav
        data-testid="large-global-nav"
        className="bg-white border-b border-grey-mid-light hidden lg:block"
      >
        <div
          className={classNames(
            `px-4 grid grid-cols-[auto_1fr_auto] gap-x-16 mx-auto h-[80px] max-w-[${containerWidth}]`,
            {
              'grid-cols-[auto_1fr_auto_auto]': buttonCta,
            },
          )}
        >
          {/* Logo */}
          {logo && (
            <Link
              href="/"
              className="w-[150px] xl:w-[166px] flex items-center"
              data-testid="lovevery-logo-desktop"
              onClick={() => {
                trackItemClickedEvent({
                  url: window.location.href,
                  type: CLICKED_ITEM_TYPES.IMAGE,
                  pageLocation: PAGE_LOCATIONS.NAVIGATION,
                  cta: CTA_NAMES.LOVEVERY_LOGO,
                });
              }}
            >
              <Image imageData={logo} alt={logo.fields.altText} />
            </Link>
          )}

          {/* Navigation groups */}
          <div className="flex items-center">
            <div className="flex items-center gap-8 xl:gap-14">
              {groups?.map((item) => (
                <div
                  className={classNames(
                    'cursor-default hover:text-blue-deep hover:underline text-base whitespace-nowrap py-3',
                    {
                      'text-blue-deep': focusedGroup?.sys.id === item.sys.id,
                    },
                  )}
                  key={item.sys.id}
                  onMouseEnter={() => handleMouseEnter(item)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.fields.title}
                </div>
              ))}
            </div>
          </div>

          {/* Button CTA */}
          {buttonCta && (
            <div
              className={classNames(
                'flex items-center justify-center opacity-0 transition-opacity duration-300',
                {
                  'opacity-100': isSticky,
                },
              )}
              data-testid="large-nav-button-cta"
            >
              <UniversalLink
                classes="btn bg-blue-deep border-blue-deep hover:bg-[#131C66] hover:border-[#131C66] active:bg-[#131C66] active:border-[#131C66] text-sm xl:text-lg px-2 xl:px-6"
                link={buttonCta}
                onClick={() => {
                  trackItemClickedEvent({
                    type: CLICKED_ITEM_TYPES.BUTTON,
                    cta: buttonCta.fields.linkText as string,
                    pageLocation: PAGE_LOCATIONS.NAVIGATION,
                    url: window.location.href,
                  });
                }}
              />
            </div>
          )}

          {/* User actions */}
          <div className="flex items-center gap-4 justify-end">
            <UserMenu
              signInLink={signInLink}
              linkMenuLoggedIn={linkMenuLoggedIn}
              translations={translations}
              hasButtonCta={!!buttonCta}
            />

            <Cart />
          </div>
        </div>
      </nav>

      {/* Focused group menu */}
      {focusedGroup?.fields.columns && (
        <div
          className="bg-white border-b border-grey-mid-light py-8 top-[81px] w-full absolute z-10  shadow-lg"
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={handleMouseLeave}
        >
          <div className="max-w-7xl mx-auto px-8">
            <div
              className="grid gap-10 justify-center"
              style={{
                gridTemplateColumns: `repeat(${Math.min(focusedGroup.fields.columns.length, MAX_NAV_COLUMN_COUNT)}, minmax(min-content, 192px))`,
              }}
            >
              {focusedGroup.fields.columns.map((column) => (
                <NavColumn
                  key={column.sys.id}
                  entryName={column.fields.entryName}
                  items={column.fields.items}
                  onClose={handleMouseLeave}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(LargeNav);
